import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SpotGeneratorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (spots: Array<{
    label: string;
    spot_type: string;
    geom_px: number[][][];
    is_active: boolean;
  }>) => void;
  imageWidth: number;
  imageHeight: number;
  scaleFtPerPixel: number;
  existingLabels: string[];
}

export function SpotGeneratorDialog({
  open,
  onOpenChange,
  onGenerate,
  imageWidth,
  imageHeight,
  scaleFtPerPixel,
  existingLabels,
}: SpotGeneratorDialogProps) {
  const [numSpots, setNumSpots] = useState(10);
  const [startingLabel, setStartingLabel] = useState(1);
  const [stallWidthFt, setStallWidthFt] = useState(9);
  const [stallDepthFt, setStallDepthFt] = useState(18);

  const handleGenerate = () => {
    // Convert feet to pixels
    const stallWidthPx = stallWidthFt / scaleFtPerPixel;
    const stallDepthPx = stallDepthFt / scaleFtPerPixel;

    // Calculate row positioned at center of image
    const centerX = imageWidth / 2;
    const centerY = imageHeight / 2;

    // Calculate total row width and starting position
    const totalWidth = numSpots * stallWidthPx;
    const startX = centerX - totalWidth / 2;
    const startY = centerY - stallDepthPx / 2;

    const spots: Array<{
      label: string;
      spot_type: string;
      geom_px: number[][][];
      is_active: boolean;
    }> = [];

    for (let i = 0; i < numSpots; i++) {
      const minX = startX + i * stallWidthPx;
      const maxX = minX + stallWidthPx;
      const minY = startY;
      const maxY = startY + stallDepthPx;

      spots.push({
        label: String(startingLabel + i),
        spot_type: 'STANDARD',
        geom_px: [[
          [minX, minY],
          [maxX, minY],
          [maxX, maxY],
          [minX, maxY],
          [minX, minY], // Close ring
        ]],
        is_active: true,
      });
    }

    onGenerate(spots);
  };

  // Find next available starting label
  const getNextLabel = () => {
    const usedNumbers = existingLabels
      .map(l => parseInt(l, 10))
      .filter(n => !isNaN(n));
    if (usedNumbers.length === 0) return 1;
    return Math.max(...usedNumbers) + 1;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate Parking Row</DialogTitle>
          <DialogDescription>
            Create a row of parking spots at the center of the image.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="numSpots">Number of Spots</Label>
              <Input
                id="numSpots"
                type="number"
                min={1}
                max={50}
                value={numSpots}
                onChange={e => setNumSpots(Math.max(1, parseInt(e.target.value) || 1))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="startingLabel">Starting Label</Label>
              <Input
                id="startingLabel"
                type="number"
                min={1}
                value={startingLabel}
                onChange={e => setStartingLabel(parseInt(e.target.value) || 1)}
              />
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => setStartingLabel(getNextLabel())}
              >
                Use next available ({getNextLabel()})
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="width">Stall Width (ft)</Label>
              <Input
                id="width"
                type="number"
                min={6}
                max={15}
                value={stallWidthFt}
                onChange={e => setStallWidthFt(parseInt(e.target.value) || 9)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="depth">Stall Depth (ft)</Label>
              <Input
                id="depth"
                type="number"
                min={10}
                max={30}
                value={stallDepthFt}
                onChange={e => setStallDepthFt(parseInt(e.target.value) || 18)}
              />
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            The row will be created at the center of the image.
            Scale: {scaleFtPerPixel.toFixed(4)} ft/pixel
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleGenerate}>
            Generate {numSpots} Spots
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
